import { Controller } from "@hotwired/stimulus"

// Tabs that survive being re-rendered.
//
// The block this drives is streamed back on every meal change, which
// destroys and rebuilds the whole thing. Without somewhere outside the
// markup to remember the choice, adding a meal would throw you back to the
// first tab mid-read.
//
// sessionStorage rather than a param or the server: it is a display
// preference, it should not be in a URL someone might share, and it should
// not survive to a new session as a surprise.
//
// The key is per-instance, so two tab groups on different pages do not
// overwrite each other's choice — the planner's summary and the account
// page's sections are unrelated, and a shared key had the second inherit a
// tab name the first had stored.
export default class extends Controller {
  static targets = ["tab", "panel", "well", "fold", "foldLabel"]
  static values = {
    key: { type: String, default: "tabs" },

    // WHETHER A FRESH PAGE LOAD KEEPS THE TAB, or starts at the first one.
    //
    // The remembering exists for RE-RENDERS: this block is streamed back on
    // every tick, and being thrown to tab one mid-read is the bug it fixed.
    // Surviving a fresh load is a different thing, and on /today it is
    // unwanted — Summary is the view that says where everything stands, and
    // someone who looked at Weight once should not open the app to a single
    // goal every morning thereafter.
    //
    // The planner's summary keeps the old behaviour: its tabs are two
    // readings of the same week rather than one goal out of several, so
    // there is nothing to get stuck in.
    resetOnLoad: { type: Boolean, default: false }
  }

  get storageKey() { return `thrive:${this.keyValue}` }

  // Beside the tab choice, and remembered for the same reason: this block is
  // streamed back on every tick, and a card that springs open when you log a
  // snack is worse than one that never folded.
  get foldKey() { return `thrive:${this.keyValue}:folded` }

  // A dataset key, so it must be camelCase and unique per tab group.
  get loadMark() {
    return `tabsLoaded${this.keyValue.replace(/[^a-z0-9]/gi, "")}`
  }

  connect() {
    // Set before show() runs, which reads it. Without a fold control there
    // is nothing to fold, so false is both the default and the only value.
    this.folded = false

    // THE MORPH PUTS THE SERVER'S TABS BACK.
    //
    // Remembering the choice in sessionStorage is not enough on its own.
    // The server renders the first panel visible and the rest `hidden`,
    // because that is the right thing to send to a browser that has not
    // chosen yet. When the day summary is morphed after a tick, Turbo
    // compares attributes: the goal panel you are reading has had `hidden`
    // removed by show(), the incoming markup still has it, so the morph
    // faithfully restores it — and the summary snaps back to the first tab
    // with the numbers you were watching correctly updated behind it.
    //
    // connect() cannot fix this afterwards: a morph does not re-connect a
    // controller whose element survived, which is the whole point of
    // morphing. So the attribute has to be defended as it is applied.
    this.guardMorph = (event) => {
      // THE ELEMENT IS event.target, NOT event.detail.target.
      //
      // Turbo passes the element to dispatch() as the event's own target
      // and puts only { attributeName, mutationType } in detail. Reading
      // `target` off detail therefore yields undefined for every attribute
      // on every element — and since `element.contains(undefined)` is
      // false, a guard written that way silently declines to guard
      // anything. It does not throw and it does not warn.
      const { attributeName } = event.detail
      const target = event.target

      // `hidden` on a panel, and `data-active`/`aria-selected` on a tab,
      // are this controller's state rather than the document's. Everything
      // else in here — the figures, the colours, the copy — is exactly what
      // the morph exists to bring in, and must not be blocked.
      // Scoped to this controller's own element, because the listener is
      // on the document and there may be more than one tab group on a
      // page — the planner's summary has its own.
      if (!this.element.contains(target)) return

      const ours =
        (attributeName === "hidden" && target.dataset?.tabsTarget === "panel") ||
        ((attributeName === "data-active" || attributeName === "aria-selected") &&
          target.dataset?.tabsTarget === "tab")

      if (ours) event.preventDefault()
    }

    // ON THE DOCUMENT, not on this.element.
    //
    // The event bubbles, so either would see it — but during a morph this
    // controller's own element may itself be the node being replaced, and
    // a listener that goes away mid-morph defends nothing. The document
    // outlives every morph there is.
    document.addEventListener("turbo:before-morph-attribute", this.guardMorph)
    let remembered = null
    try {
      remembered = sessionStorage.getItem(this.storageKey)

      // A FRESH LOAD FORGETS; A RE-RENDER REMEMBERS.
      //
      // Both arrive here as connect(), so they are told apart by a marker
      // on the document: the first connect after a page load finds none and
      // clears the stored tab, every later one finds it and keeps the
      // choice. That is what lets a stream re-render hold your place while
      // opening the page still starts at Summary.
      if (this.resetOnLoadValue && !document.documentElement.dataset[this.loadMark]) {
        document.documentElement.dataset[this.loadMark] = "1"
        sessionStorage.removeItem(this.storageKey)
        remembered = null
      }
    } catch {
      // Private browsing, or storage disabled. The first tab is a fine
      // default and nothing else here depends on it.
    }

    const name = remembered && this.hasTab(remembered) ? remembered : this.firstTab
    this.show(name)

    // OPTIONAL, because the planner's summary has no fold. A controller that
    // required the targets would break its other caller.
    if (this.hasWellTarget) this.applyFold(this.rememberedFold)

    // A REMEMBERED TAB HAS TO BE VISIBLE TO READ AS SELECTED.
    //
    // Once the strip scrolls, a remembered fifth tab is chosen off-screen:
    // the panel below is right, the strip shows tabs one to four with none
    // of them active, and the card looks broken rather than scrolled.
    //
    // Only on connect, and only when the strip actually scrolls. Doing it in
    // show() would yank the strip on every click, including the ones you
    // made by clicking a tab you could already see.
    this.revealTab(name)
  }

  // `nearest` rather than `center`: an already-visible tab must not move at
  // all, which is the common case. Guarded because scrollIntoView with
  // options is not universal and a strip that does not scroll needs nothing.
  revealTab(name) {
    const tab = this.tabTargets.find((candidate) => candidate.dataset.tab === name)
    const strip = tab?.parentElement
    if (!tab || !strip) return

    // Nothing to scroll to, nothing to do. (This used to set a data-fits
    // attribute for a fade mask on the strip; the mask clipped the active
    // tab's underline, which sits a pixel outside the strip's box, so it
    // was removed — see the stylesheet.)
    if (strip.scrollWidth <= strip.clientWidth) return

    try {
      tab.scrollIntoView({ block: "nearest", inline: "nearest" })
    } catch {
      // Older engines take no options and would scroll the PAGE to the tab,
      // which is worse than leaving the strip where it is.
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-morph-attribute", this.guardMorph)
  }

  select(event) {
    const name = event.currentTarget.dataset.tab
    const wasActive = this.activeTab === name

    this.show(name)

    try {
      sessionStorage.setItem(this.storageKey, name)
    } catch {
      // As above — the tab still switches, it just will not be remembered.
    }

    // PICKING A DIFFERENT TAB OPENS THE CARD. Choosing one and being shown
    // nothing is a click that appears not to have worked — and wanting to
    // read a different goal is the commonest reason to open a folded card.
    //
    // Only for a DIFFERENT tab: clicking the one already selected leaves
    // the fold alone, so the strip does not become a second way to expand
    // that fights the control beside it.
    if (this.folded && !wasActive) this.toggle()
  }

  show(name) {
    this.panelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.tab !== name
    })

    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.tab === name
      tab.dataset.active = active
      tab.setAttribute("aria-selected", active)

      // EVERY TAB STAYS ON THE STRIP, folded or not.
      //
      // Collapsing used to hide all but the active one, on the reasoning
      // that the others are choices about content that is not on screen.
      // That made switching a two-step job: expand, then pick — and picking
      // a different tab is the commonest reason to open a folded card at
      // all. Clicking Weight while collapsed now selects it directly, and
      // the panel is one click away rather than two.
    })
  }

  // ── The fold ────────────────────────────────────────────────────────────
  //
  // NOT a <details>. The thing being toggled contains its own tab buttons,
  // and interactive controls inside a <summary> are invalid — so it is a
  // button with aria-expanded pointing at the panel well's id.

  toggle() {
    this.applyFold(!this.folded)

    try {
      sessionStorage.setItem(this.foldKey, this.folded ? "1" : "0")
    } catch {
      // As with the tab choice: it still folds, it just will not be
      // remembered across a re-render.
    }
  }

  applyFold(folded) {
    this.folded = folded
    this.wellTarget.hidden = folded
    this.foldTarget.setAttribute("aria-expanded", String(!folded))

    // COLLAPSED STILL NAMES WHAT IS FOLDED: the active tab stays on the
    // strip beside the control. A bare caret over an empty bar would be a
    // mystery box.

    // The word only while folded: a lone caret on a collapsed card is a
    // control you have to try to understand, and the card is what is hidden
    // behind it. Open, the panel below makes the caret obvious.
    if (this.hasFoldLabelTarget) {
      this.foldLabelTarget.classList.toggle("hidden", !folded)
    }
    this.foldTarget.setAttribute("aria-label", folded ? "Expand" : "Minimise")

    this.show(this.activeTab)
  }

  get rememberedFold() {
    try {
      return sessionStorage.getItem(this.foldKey) === "1"
    } catch {
      return false
    }
  }

  get activeTab() {
    return this.tabTargets.find((tab) => tab.dataset.active === "true")?.dataset.tab ||
      this.firstTab
  }

  hasTab(name) {
    return this.tabTargets.some((tab) => tab.dataset.tab === name)
  }

  get firstTab() {
    return this.tabTargets[0]?.dataset.tab
  }
}
